import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BellIcon } from '@/components/icons/BellIcon';
import { ArrowLeftIcon } from '@/components/icons/ArrowLeftIcon';
import { apiGetRequest, apiPostRequest } from '@/utils/apiClient';
import { useNotificationSidebar, Notification } from '@/contexts/NotificationSidebarContext';
import Modal from '@/components/Modal';
import DOMPurify from 'dompurify';

/**
 * 通知一覧コンポーネント
 */
const NotificationList: React.FC = () => {
    const { t, i18n } = useTranslation('notifications');
    const {
        isOpen,
        setHasUnread,
        notifications,
        setNotifications,
        isLoadingNotifications,
        setIsLoadingNotifications,
    } = useNotificationSidebar();
    const [selectedNotificationId, setSelectedNotificationId] = useState<number | null>(null);
    const [modalNotificationId, setModalNotificationId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const fetchInProgressRef = useRef(false);

    // お知らせを取得（右サイドバーが開いている時のみ、かつ既に取得済みでない場合のみ）
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        // 既にデータがある場合はスキップ（SPAなので再取得不要）
        if (notifications.length > 0) {
            return;
        }

        // 既に取得中の場合はスキップ
        if (fetchInProgressRef.current) {
            return;
        }

        const fetchNotifications = async () => {
            if (fetchInProgressRef.current) {
                return;
            }

            fetchInProgressRef.current = true;
            try {
                setIsLoadingNotifications(true);
                setError(null);
                const response = await apiGetRequest('/api/notifications');

                if (!response.ok) {
                    // 429エラーの場合は特別に処理
                    if (response.status === 429) {
                        setError('リクエストが多すぎます。しばらく待ってから再度お試しください。');
                        return;
                    }
                    throw new Error('Failed to fetch notifications');
                }

                const data: Notification[] = await response.json();
                setNotifications(data);

                // 未読のお知らせがあるかチェック
                const hasUnread = data.some((n) => !n.read);
                setHasUnread(hasUnread);
            } catch (err) {
                console.error('Error fetching notifications:', err);
                setError(err instanceof Error ? err.message : 'Unknown error');
            } finally {
                setIsLoadingNotifications(false);
                fetchInProgressRef.current = false;
            }
        };

        fetchNotifications();
    }, [isOpen, notifications.length, setHasUnread, setNotifications, setIsLoadingNotifications]);

    /**
     * お知らせを既読にする
     */
    const markAsRead = async (notificationId: number) => {
        try {
            const response = await apiPostRequest('/api/notifications/read', {
                notificationId: notificationId,
            });

            if (!response.ok) {
                throw new Error('Failed to mark as read');
            }

            // 既読状態を更新
            setNotifications((prev: Notification[]) => {
                const updated = prev.map((n: Notification) => (n.id === notificationId ? { ...n, read: true } : n));
                // 未読のお知らせがあるかチェック
                const hasUnread = updated.some((n: Notification) => !n.read);
                setHasUnread(hasUnread);
                return updated;
            });
        } catch (err) {
            console.error('Error marking as read:', err);
        }
    };

    /**
     * 日付を月日形式でフォーマット
     */
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        // 現在の言語に応じたロケールを決定
        const locale = i18n.language?.startsWith('ja') ? 'ja-JP' : 'en-US';
        return date.toLocaleDateString(locale, {
            month: 'long',
            day: 'numeric',
        });
    };

    /**
     * HTMLタグを除去してプレーンテキストを取得（一覧表示用）
     */
    const stripHtml = (html: string): string => {
        // DOMPurifyでHTMLタグを除去
        const clean = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
        return clean.trim();
    };

    const selectedNotification = notifications.find((n) => n.id === selectedNotificationId);
    const modalNotification = notifications.find((n) => n.id === modalNotificationId);

    const handleNotificationClick = async (id: number) => {
        const notification = notifications.find((n) => n.id === id);
        if (!notification) return;

        // display_typeがmodalの場合はモーダルで表示
        if (notification.displayType === 'modal') {
            setModalNotificationId(id);
            // 未読の場合、既読にする
            if (!notification.read) {
                await markAsRead(id);
            }
        } else {
            // 通常のお知らせは詳細表示
            setSelectedNotificationId(id);
            // 未読の場合、既読にする
            if (!notification.read) {
                await markAsRead(id);
            }
        }
    };

    const handleBackClick = () => {
        setSelectedNotificationId(null);
    };

    const handleCloseModal = () => {
        setModalNotificationId(null);
    };

    if (isLoadingNotifications && notifications.length === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <p className="text-sm text-gray-500">{t('loading', '読み込み中...')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <p className="text-sm text-red-500">{t('error', 'エラーが発生しました')}</p>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col h-full overflow-hidden bg-white">
                {/* ヘッダー */}
                <div className="pl-6 pr-[52px] py-4 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        {selectedNotificationId ? (
                            <button
                                onClick={handleBackClick}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                                aria-label={t('back')}
                            >
                                <ArrowLeftIcon className="size-5 text-gray-700" />
                            </button>
                        ) : (
                            <BellIcon className="size-5 text-gray-700" />
                        )}
                        <h2 className="text-sm font-bold text-gray-700 break-words">
                            {selectedNotificationId ? selectedNotification?.title : t('title')}
                        </h2>
                    </div>
                </div>

                {/* コンテンツ */}
                <div className="flex-1 overflow-y-auto min-h-0">
                    {selectedNotificationId && selectedNotification ? (
                        /* 詳細表示 */
                        <div className="px-6 py-4">
                            <div className="space-y-4">
                                <div>
                                    <div
                                        className="text-sm text-gray-600 leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: selectedNotification.body }}
                                    />
                                </div>
                                <div className="pt-4 border-t border-gray-200">
                                    <p className="text-xs text-gray-400">{formatDate(selectedNotification.publishStartAt)}</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* 一覧表示 */
                        notifications.length === 0 ? (
                            <div className="flex items-center justify-center h-full text-gray-500">
                                <p className="text-sm">{t('empty')}</p>
                            </div>
                        ) : (
                            <div>
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification.id)}
                                        className={`
                                            m-2 px-6 py-4 transition-colors cursor-pointer rounded-md border-b border-gray-200
                                            ${notification.read ? 'bg-gray-50' : 'bg-white'}
                                            ${notification.read ? 'hover:bg-gray-100' : 'hover:bg-gray-50'}
                                        `}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="flex-1 min-w-0">
                                                <h3 className={`text-sm font-semibold mb-1 ${notification.read ? 'text-gray-400' : 'text-gray-900'}`}>
                                                    {notification.title}
                                                </h3>
                                                <p className={`text-sm mb-2 line-clamp-1 ${notification.read ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    {stripHtml(notification.body)}
                                                </p>
                                                <p className="text-xs text-gray-400">{formatDate(notification.publishStartAt)}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </div>
            </div>

            {/* モーダル表示（display_typeがmodalの場合） */}
            {modalNotification && (
                <Modal
                    isOpen={modalNotificationId !== null}
                    onClose={handleCloseModal}
                    contentClassName="max-w-2xl w-full p-6"
                    hideCloseButton={true}
                >
                    <div className="flex flex-col h-full">
                        {/* タイトル（固定レイアウト） */}
                        <h2 className="text-lg font-bold text-gray-900 mb-4 flex-shrink-0">
                            {modalNotification.title}
                        </h2>
                        {/* HTMLコンテンツ */}
                        <div
                            className="flex-1 overflow-y-auto text-sm text-gray-600 leading-relaxed mb-6"
                            dangerouslySetInnerHTML={{ __html: modalNotification.body }}
                        />
                        {/* 閉じるボタン（下の中央） */}
                        <div className="flex justify-center flex-shrink-0">
                            <button
                                onClick={handleCloseModal}
                                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition-colors"
                            >
                                {t('close')}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default NotificationList;
